import React, { useState } from "react";
import { useHistory, Link, Redirect } from "react-router-dom";
import jwt from "jwt-decode";
import notifications from "../../common/Components/Notifications/Notification";
import { postApi } from "../../helpers/auth_helpers";
import {
  clearStudioWorkspaceCaches,
  isStudioLoggedIn,
  markStudioSignupNudge,
} from "../../helpers/studioSignupSession";
import SiteLayout from "../website/site/SiteLayout";
import loomImg from "../website/assets/images/site/front-page-1-3.jpg";

const DRAFT_KEY = "hkp.studioSignup.draft";
const emptyForm = {
  name: "",
  organizationName: "",
  email: "",
  phone: "",
  password: "",
  acceptedTerms: false,
};

const EMAIL_PATTERN = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Accept what phones autofill ("+91 98765 43210", "098765-43210") and keep the 10-digit number. */
export const normalizeStudioPhone = (raw: string) => {
  const digits = raw.replace(/\D/g, "");
  if (digits.length === 12 && digits.startsWith("91")) return digits.slice(2);
  if (digits.length === 11 && digits.startsWith("0")) return digits.slice(1);
  return digits;
};

const readDraft = () => {
  try {
    const saved = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || "null");
    if (!saved || typeof saved !== "object") return emptyForm;
    return {
      name: String(saved.name || ""),
      organizationName: String(saved.organizationName || ""),
      email: String(saved.email || ""),
      phone: String(saved.phone || ""),
      // The password is never kept in the draft.
      password: "",
      acceptedTerms: Boolean(saved.acceptedTerms),
    };
  } catch {
    return emptyForm;
  }
};

const BENEFITS: [string, string][] = [
  ["7 days", "Every Studio module free — Catalogue, Karkhana, Orders, AI and more"],
  ["Core", "Products, certificates and NFC / QR tags stay on after the trial"],
  ["No card", "Nothing to pay to start. Keep only the modules you use"],
];

type FieldProps = {
  id: string;
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: React.ReactNode;
};

const Field = ({ id, label, error, hint, required = false, children }: FieldProps) => (
  <div className={`su-field${error ? " has-error" : ""}`}>
    <label htmlFor={id}>
      {label}
      {required && <span className="su-req" aria-hidden="true">*</span>}
    </label>
    {children}
    {error ? (
      <p className="su-error" id={`${id}-error`} role="alert">{error}</p>
    ) : (
      hint && <p className="su-hint" id={`${id}-hint`}>{hint}</p>
    )}
  </div>
);

const StudioSignup = () => {
  const history = useHistory();
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(readDraft);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [response, setResponse] = useState("");
  const signedIn = isStudioLoggedIn();

  const onChange = (name: string, value: string | boolean) => {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      const { password, ...draft } = next;
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
      return next;
    });
    setErrors((prev) => ({ ...prev, [name]: "" }));
    setResponse("");
  };

  const validate = () => {
    const next: Record<string, string> = {};
    if (!form.name.trim()) next.name = "Your name is required.";
    if (!form.organizationName.trim()) next.organizationName = "Organisation name is required.";
    if (!form.email.trim()) next.email = "Email is required.";
    else if (!EMAIL_PATTERN.test(form.email.trim())) next.email = "Enter a valid email address.";
    if (!/^[0-9]{10}$/.test(normalizeStudioPhone(form.phone))) {
      next.phone = "Enter a 10-digit phone number.";
    }
    if (!form.password || form.password.length < 8) {
      next.password = "Password must be at least 8 characters.";
    }
    if (!form.acceptedTerms) next.acceptedTerms = "Accept the terms to continue.";
    setErrors(next);
    if (Object.keys(next).length > 0) {
      window.setTimeout(() => {
        const first = document.querySelector<HTMLElement>(".su-field.has-error input, .su-terms.has-error input");
        first?.focus();
        first?.scrollIntoView({ block: "center" });
      }, 0);
    }
    return Object.keys(next).length === 0;
  };

  const handleSubmit = () => {
    if (loading || !validate()) return;
    setLoading(true);
    const url = process.env.REACT_APP_SERVERURL + "/auth/studio-signup";
    postApi(url, {
      name: form.name.trim(),
      organizationName: form.organizationName.trim(),
      email: form.email.trim().toLowerCase(),
      phone: normalizeStudioPhone(form.phone),
      password: form.password,
      acceptedTerms: true,
    })
      .then((res: any) => {
        setLoading(false);
        if (res?.success && res?.data?.token) {
          const authUser: any = jwt(res.data.token);
          localStorage.setItem("authUser", JSON.stringify(authUser));
          localStorage.setItem("token", res.data.token);
          localStorage.setItem("refreshToken", res.data.refreshToken);
          clearStudioWorkspaceCaches();
          sessionStorage.removeItem(DRAFT_KEY);
          markStudioSignupNudge();
          notifications({
            title: "Success!",
            message: "Studio created. You can add GST and address next.",
            type: "success",
          });
          history.push("/dashboard");
        } else {
          setResponse(res?.message || "Please check the form and try again.");
        }
      })
      .catch(() => {
        setLoading(false);
        setResponse("Studio signup is not available right now. Please try again in a few minutes.");
      });
  };

  // Already in Studio: signing up again would replace the current session, so go to the dashboard.
  if (signedIn) {
    return <Redirect to="/dashboard" />;
  }

  const describedBy = (id: string, key: string, hint?: boolean) =>
    errors[key] ? `${id}-error` : hint ? `${id}-hint` : undefined;

  return (
    <SiteLayout title="Start your free studio — HastKala Pramanak" minimal>
      <section className="su">
        <div className="s-wrap su__grid">
          <div className="su__card">
            <span className="s-eyebrow">Studio · 7-day free trial</span>
            <h1>Create your studio</h1>
            <p className="su__lead">
              You'll be the organisation admin. GST, address and logo can wait until you're inside.
            </p>
            <p className="su__required-note"><span className="su-req" aria-hidden="true">*</span> Required</p>

            {response && (
              <p className="su-banner" role="alert">{response}</p>
            )}

            <form
              noValidate
              onSubmit={(event) => {
                event.preventDefault();
                handleSubmit();
              }}
            >
              <div className="su__row">
                <Field id="su-name" label="Your name" error={errors.name} required>
                  <input
                    id="su-name"
                    name="name"
                    autoComplete="name"
                    value={form.name}
                    onChange={(e) => onChange("name", e.target.value)}
                    required
                    aria-invalid={Boolean(errors.name)}
                    aria-describedby={describedBy("su-name", "name")}
                  />
                </Field>
                <Field id="su-org" label="Organisation / studio name" error={errors.organizationName} required>
                  <input
                    id="su-org"
                    name="organization"
                    autoComplete="organization"
                    value={form.organizationName}
                    onChange={(e) => onChange("organizationName", e.target.value)}
                    required
                    aria-invalid={Boolean(errors.organizationName)}
                    aria-describedby={describedBy("su-org", "organizationName")}
                  />
                </Field>
              </div>

              <Field id="su-email" label="Email" error={errors.email} required>
                <input
                  id="su-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  value={form.email}
                  onChange={(e) => onChange("email", e.target.value)}
                  required
                  aria-invalid={Boolean(errors.email)}
                  aria-describedby={describedBy("su-email", "email")}
                />
              </Field>

              <Field id="su-phone" label="Phone" error={errors.phone} hint="10-digit mobile. +91 is fine." required>
                <div className="su-prefix">
                  <span aria-hidden="true">+91</span>
                  <input
                    id="su-phone"
                    name="tel"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel-national"
                    value={form.phone}
                    onChange={(e) => onChange("phone", e.target.value)}
                    required
                    aria-invalid={Boolean(errors.phone)}
                    aria-describedby={describedBy("su-phone", "phone", true)}
                  />
                </div>
              </Field>

              <Field id="su-password" label="Password" error={errors.password} hint="At least 8 characters." required>
                <div className="su-password">
                  <input
                    id="su-password"
                    name="new-password"
                    autoComplete="new-password"
                    type={show ? "text" : "password"}
                    value={form.password}
                    onChange={(e) => onChange("password", e.target.value)}
                    required
                    aria-invalid={Boolean(errors.password)}
                    aria-describedby={describedBy("su-password", "password", true)}
                  />
                  <button
                    type="button"
                    onClick={() => setShow(!show)}
                    aria-label={show ? "Hide password" : "Show password"}
                    aria-pressed={show}
                  >
                    {show ? "Hide" : "Show"}
                  </button>
                </div>
              </Field>

              <div className={`su-terms${errors.acceptedTerms ? " has-error" : ""}`}>
                <label>
                  <input
                    type="checkbox"
                    checked={form.acceptedTerms}
                    onChange={(e) => onChange("acceptedTerms", e.target.checked)}
                    required
                    aria-invalid={Boolean(errors.acceptedTerms)}
                  />
                  <span>
                    <span className="su-req" aria-hidden="true">*</span>I accept the <Link to="/terms-and-conditions" target="_blank">Terms of service</Link> and{" "}
                    <Link to="/privacy-policy" target="_blank">Privacy Policy</Link>.
                  </span>
                </label>
                {errors.acceptedTerms && <p className="su-error" role="alert">{errors.acceptedTerms}</p>}
              </div>

              <button type="submit" className="s-btn s-btn--primary su__submit" disabled={loading} aria-busy={loading}>
                {loading ? "Creating your studio…" : "Create studio — start free trial"}
              </button>
            </form>

            <p className="su__alt">
              Already have Studio access? <Link to="/login">Log in</Link>
              <span aria-hidden="true"> · </span>
              <Link to="/studio">What's in Studio</Link>
            </p>
          </div>

          <aside className="su__side" aria-label="What you get">
            <img src={loomImg} alt="A weaver's hands working at a handloom" />
            <div className="su__benefits">
              {BENEFITS.map(([big, text]) => (
                <div key={big}><b>{big}</b><span>{text}</span></div>
              ))}
            </div>
          </aside>
        </div>
      </section>
    </SiteLayout>
  );
};

export default StudioSignup;
